import { css } from "@emotion/css";
import Modal from "patient-ping-remedy/packages/modal";
import React, {useContext, useMemo, useState} from "react";
import Button, { StyleType } from "patient-ping-remedy/packages/button";
import Input from "patient-ping-remedy/packages/input";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ToastType } from "patient-ping-remedy/packages/toast";
import { AxiosError } from "axios";
import Helpers from "@/helpers/helpers";
import { CarecoApiContext } from "@/app-context/careco-api-context";
import { useAlertStore } from "@/store/alert_store";

interface Props {
  close: () => void;
  tableData: { category: { value: string } }[];
}

export default function CreateFormFieldCategoryModal({ close, tableData }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const { addAlert } = useAlertStore();
  const [categoryName, setCategoryName] = useState<string>('');

  const isValid = useMemo(() => {
    if (!categoryName) return true;
    const re = /^[A-Za-z0-9_'-]{2,200}$/;
    return re.test(categoryName);
  }, [categoryName]);

  const { mutate: addFormFieldCategory, isPending: isAdding } = useMutation({
    mutationFn: (name: string) => carecoApi!.createFormFieldCategory(name),
    onSuccess: () => {
      addAlert({ content: "Form field category added successfully.", type: ToastType.SUCCESS });
      void queryClient.invalidateQueries({ queryKey: ["formFieldCategories"] });
      close();
    },
    onError: (error: AxiosError) => {
      Helpers.alertOnError(`Failed to add form field category. ${Helpers.traceId(error)}`, error);
    },
  });

  const handleAddCategory = () => {
    addFormFieldCategory(categoryName);
  };

  const isCategoryNameTaken = useMemo(() => {
    return tableData.some((data) => data.category.value === categoryName);
  }, [categoryName, tableData]);

  const isAddDisabled =
    isCategoryNameTaken
    || isAdding
    || !categoryName
    || !isValid;

  return (
    <Modal
      className={css({ width: "70rem" })}
      headerText="Create form field category"
      buttons={[
        <Button key="cancel" onClick={close} styleType={StyleType.TERTIARY}>
          Cancel
        </Button>,
        <Button
          testId="add-form-field-category-modal-button"
          key="add"
          onClick={handleAddCategory}
          disabled={isAddDisabled}
        >
          Add
        </Button>,
      ]}
    >
      <Input
        value={categoryName}
        id="form-field-category"
        labelText="Field category"
        required="required"
        onChange={(e) => setCategoryName(e.target.value)}
        errorMessage={isCategoryNameTaken ? "Field category already exists" :
          "The field category must be between 2 and 200 characters long "
          + "containing only letters (A-Z, a-z), numbers (0-9), underscores (_),"
          + "and/or hyphens (-)."}
        error={!isValid || isCategoryNameTaken}
      />
    </Modal>
  );
}
